/**
 * Thin wrapper over the raw WebSocket from socket.ts that adds:
 *   - typed send (just JSON.stringify the action body)
 *   - per-server-type listener registry
 *   - subscribe-on-open lifecycle so the caller doesn't have to care about
 *     the readyState dance
 *
 * This is intentionally NOT a full reconnecting client — that lives in a
 * later slice once the live state machine settles. For now, the page that
 * opens the socket also owns its lifetime; close on unmount, reopen on
 * navigation. The HTTP read path is the source of truth on every page load.
 */
import { openSocket } from './socket';
import { SceneActions } from '../vtt/net/sceneWire';
import type { ServerEnvelope } from './runtimeProtocol';

type Listener = (payload: unknown, envelope: ServerEnvelope<unknown>) => void;

export interface CampaignSocketOptions {
  sessionId: string;
  /** Map id to subscribe to. The Sheet page passes the campaign's first map. */
  mapId: string;
}

export class CampaignSocket {
  private ws: WebSocket | null = null;
  private listeners = new Map<string, Set<Listener>>();
  private pendingSends: string[] = [];
  private closed = false;

  /**
   * Open the socket and send a scene/subscribe so the connection row gets
   * its SessionId populated — the BySession GSI is what makes the runtime
   * broadcasts reach this client. Resolves once the WebSocket is OPEN; the
   * subscribe message is queued before that and flushed on open.
   */
  static async connect(opts: CampaignSocketOptions): Promise<CampaignSocket> {
    const cs = new CampaignSocket();
    const ws = await openSocket();
    cs.ws = ws;

    ws.addEventListener('open', () => {
      // Subscribe immediately so SessionId is bound on the connection row
      // before any runtime mutations need to fan out to us.
      cs.send({
        action: SceneActions.Subscribe,
        sessionId: opts.sessionId,
        mapId: opts.mapId,
      });
      // Flush anything the caller queued before open.
      for (const msg of cs.pendingSends) {
        ws.send(msg);
      }
      cs.pendingSends = [];
    });

    ws.addEventListener('message', (ev) => {
      let envelope: ServerEnvelope<unknown> | null = null;
      try {
        envelope = JSON.parse(ev.data) as ServerEnvelope<unknown>;
      } catch {
        // Non-JSON messages aren't part of the protocol — ignore.
        return;
      }
      if (!envelope || typeof envelope.type !== 'string') return;
      const set = cs.listeners.get(envelope.type);
      if (!set) return;
      for (const fn of set) {
        try {
          fn(envelope.payload, envelope);
        } catch (err) {
          // A buggy listener should not silently break the rest of the chain.
          // eslint-disable-next-line no-console
          console.error('campaignSocket listener error', err);
        }
      }
    });

    ws.addEventListener('close', () => {
      cs.closed = true;
    });

    return cs;
  }

  /** Register a listener for a server envelope type. Returns an unsubscribe fn. */
  on<T = unknown>(type: string, fn: (payload: T, envelope: ServerEnvelope<T>) => void): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    const wrapped: Listener = (p, e) => fn(p as T, e as ServerEnvelope<T>);
    set.add(wrapped);
    return () => {
      set!.delete(wrapped);
    };
  }

  /** Send an action body. Queues until the socket reaches OPEN. */
  send(body: object): void {
    if (this.closed) return;
    const json = JSON.stringify(body);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(json);
    } else {
      this.pendingSends.push(json);
    }
  }

  close(): void {
    this.closed = true;
    this.ws?.close();
    this.listeners.clear();
  }
}
